import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { streamRunEvents } from '../services/api';
import { botKeys } from '../lib/queryKeys';
import { WORKSPACE_TOOLS } from '../components/bots/botActivity';

const ACTIVE = new Set(['queued', 'running']);

/**
 * Live progress of a bot's active runs: text streamed so far (every part of the answer,
 * also what was written before a tool), the tool the bot is using right now and the steps
 * it took, keyed by run id; computerWait: the computers keeping it waiting while its plan runs
 * all it may at once. When a run ends the conversation and task views are refetched so the
 * stored message replaces the live text. lastAt: when the run last sent anything, so
 * a long silence (a slow or stalled model call) can be told apart from work going on.
 */
const useBotRunStreams = (runs) => {
  const queryClient = useQueryClient();
  const [live, setLive] = useState({});
  const streams = useRef({});
  // The last event seen of each run. A run that waits for the person stops being followed and
  // is followed again when it goes on: from there, or its text and steps showed twice.
  const seen = useRef({});

  const activeIds = (runs || []).filter((r) => ACTIVE.has(r.status)).map((r) => r.id);
  const activeKey = activeIds.join(',');

  useEffect(() => {
    const wanted = new Set(activeKey ? activeKey.split(',') : []);
    wanted.forEach((runId) => {
      if (streams.current[runId]) return;
      streams.current[runId] = streamRunEvents(runId, (event, id) => {
        if (id) seen.current[runId] = id;
        setLive((prev) => {
          const cur = { ...(prev[runId] || { text: '', tool: null, status: 'queued' }), lastAt: Date.now() };
          if (event.type === 'delta') {
            // Text of a new model call after a tool is a new paragraph of the same answer: the
            // server keeps every part (agents/conversation.py), so nothing written disappears.
            const gap = cur.newPart && cur.text ? '\n\n' : '';
            return { ...prev, [runId]: { ...cur, text: cur.text + gap + event.text, newPart: false } };
          }
          if (event.type === 'step') return { ...prev, [runId]: { ...cur, newPart: true } };
          if (event.type === 'computer_wait') {
            return { ...prev, [runId]: { ...cur, computerWait: { cap: event.cap, running: event.running } } };
          }
          if (event.type === 'tool_start') {
            let args = null;
            try { args = JSON.parse(event.args || '{}'); } catch { /* partial or not JSON */ }
            const action = { tool: event.tool, args, at: Date.now(), callId: event.call_id };
            return { ...prev, [runId]: { ...cur, tool: event.tool, newPart: true, action, steps: [...(cur.steps || []), action] } };
          }
          if (event.type === 'tool_end') {
            // Reads run at the same time: the step that ended is found by its call id.
            const steps = [...(cur.steps || [])];
            let i = event.call_id ? steps.findIndex((s) => s.callId === event.call_id && !s.done) : -1;
            if (i < 0) i = steps.findIndex((s) => s.tool === event.tool && !s.done);
            if (i >= 0) steps[i] = { ...steps[i], ok: event.ok !== false, done: true };
            const busyTool = steps.slice().reverse().find((s) => !s.done)?.tool || null;
            return { ...prev, [runId]: { ...cur, tool: busyTool, steps, computerWait: null } };
          }
          if (event.type === 'status') return { ...prev, [runId]: { ...cur, status: event.status } };
          if (event.type === 'done' || event.type === 'waiting') {
            return { ...prev, [runId]: { ...cur, status: event.status || 'done', tool: null, stoppedBy: event.stopped_by || null } };
          }
          return prev;
        });
        if (event.type === 'tool_start' && WORKSPACE_TOOLS.has(event.tool)) {
          queryClient.invalidateQueries({ queryKey: [...botKeys.all, 'workspace'] });
        }
        if (event.type === 'memory') {
          queryClient.invalidateQueries({ queryKey: [...botKeys.all, 'memory'] });
        }
        if (event.type === 'steered') {  // the bot read what was written meanwhile (agents/steering.py)
          queryClient.invalidateQueries({ queryKey: [...botKeys.all, 'chat'] });
        }
        if (event.type === 'done' || event.type === 'waiting') {
          queryClient.invalidateQueries({ queryKey: botKeys.all });
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
        }
      }, seen.current[runId]);
    });
    Object.keys(streams.current).forEach((runId) => {
      if (!wanted.has(runId)) {
        streams.current[runId]();
        delete streams.current[runId];
      }
    });
  }, [activeKey, queryClient]);

  useEffect(() => () => {
    Object.values(streams.current).forEach((stop) => stop());
    streams.current = {};
  }, []);

  return live;
};

export default useBotRunStreams;
